/**
 * NavigationDock.ts
 * Bottom persistent navigation dock for American Dream: Pocket Life.
 * Features:
 * - 4 accessible touch-friendly buttons: US Map, Decor, Shortcuts/Check-in, Comeback Home
 * - iOS Safe Area handling
 * - 250ms touch debouncing
 * - Contextual highlighting (pulsing emerald glow on Comeback Home when in MapScene)
 */

import { EventBus } from '../core/EventBus';
import { renderUiGouacheIcon } from './Icons';
import { preventButtonFocusScroll } from './ScrollPreserver';

export type ActiveSceneId = 'HomeScene' | 'MapScene' | 'WorldMapScene' | 'CountyDioramaScene';

export class NavigationDock {
  private container: HTMLElement;
  private btnMap!: HTMLButtonElement;
  private btnDecor!: HTMLButtonElement;
  private btnShortcuts!: HTMLButtonElement;
  private btnHome!: HTMLButtonElement;

  private currentScene: ActiveSceneId = 'HomeScene';
  private isDecorModeActive: boolean = false;
  private lastTapTimestamp: number = 0;
  private readonly DEBOUNCE_MS: number = 250;
  private unsubscribes: Array<() => void> = [];

  constructor(containerId: string = 'nav-dock-container') {
    const el = document.getElementById(containerId) || document.getElementById('bottom-nav-bar');
    if (!el) {
      throw new Error(`[NavigationDock] Mount element #${containerId} not found`);
    }
    const prevCleanup = (el as unknown as { __navDockCleanup?: () => void }).__navDockCleanup;
    if (typeof prevCleanup === 'function') {
      prevCleanup();
    }
    this.container = el;
    this.render();
    this.bindEvents();
    (this.container as unknown as { __navDockCleanup?: () => void }).__navDockCleanup = () =>
      this.destroy();
    this.updateSceneState('HomeScene');
  }

  private render(): void {
    this.container.innerHTML = `
      <div class="nav-corner-slot nav-corner-slot--left">
        <button type="button" id="btn-nav-map" class="nav-btn nav-btn--hero-left" aria-label="Bản đồ 50 tiểu bang Mỹ">
          <span class="nav-icon nav-icon--hero">${renderUiGouacheIcon('icon_atlas_compass', { size: 30, className: 'nav-gouache-icon nav-gouache-icon--hero' })}</span>
          <span class="nav-text-col">
            <span class="nav-label">Bản đồ Mỹ</span>
            <span class="nav-sublabel tabular-nums">50 Bang • Khám phá</span>
          </span>
        </button>

        <button type="button" id="btn-nav-home" class="nav-btn nav-btn--hero-left nav-btn-hidden" aria-label="Quay về phòng Home">
          <span class="nav-icon nav-icon--hero">${renderUiGouacheIcon('icon_home_cottage', { size: 30, className: 'nav-gouache-icon nav-gouache-icon--hero' })}</span>
          <span class="nav-text-col">
            <span class="nav-label">Về Phòng</span>
            <span class="nav-sublabel tabular-nums">Tổ ấm 2.5D</span>
          </span>
        </button>
      </div>

      <div class="nav-corner-slot nav-corner-slot--right">
        <button type="button" id="btn-nav-decor" class="nav-btn nav-btn--corner-right" aria-label="Chế độ trang trí phòng">
          <span class="nav-icon">${renderUiGouacheIcon('icon_decor_palette', { size: 28, className: 'nav-gouache-icon' })}</span>
          <span class="nav-label">Trang trí</span>
        </button>

        <button type="button" id="btn-nav-shortcuts" class="nav-btn nav-btn--corner-right nav-btn-hidden" aria-label="Phím tắt Apple Shortcuts và check-in">
          <span class="nav-icon">${renderUiGouacheIcon('icon_gps_checkin', { size: 28, className: 'nav-gouache-icon' })}</span>
          <span class="nav-label">Check-in</span>
          <span id="nav-shortcuts-badge" class="nav-badge tabular-nums hidden">!</span>
        </button>
      </div>
    `;

    this.btnMap = this.container.querySelector('#btn-nav-map')!;
    this.btnDecor = this.container.querySelector('#btn-nav-decor')!;
    this.btnShortcuts = this.container.querySelector('#btn-nav-shortcuts')!;
    this.btnHome = this.container.querySelector('#btn-nav-home')!;
    preventButtonFocusScroll(this.container);
  }

  private bindEvents(): void {
    this.btnMap.addEventListener('click', () => this.handleDebouncedTap(() => this.onMapTap()));
    this.btnDecor.addEventListener('click', () => this.handleDebouncedTap(() => this.onDecorTap()));
    this.btnShortcuts.addEventListener('click', () => this.handleDebouncedTap(() => this.onShortcutsTap()));
    this.btnHome.addEventListener('click', () => this.handleDebouncedTap(() => this.onHomeTap()));

    // Listen to scene change notifications from Phaser / Game state
    this.unsubscribes.push(
      EventBus.on('SCENE_CHANGED', (sceneId: ActiveSceneId) => {
        this.updateSceneState(sceneId);
      }),
      EventBus.on('DECOR_MODE_CHANGED', (isActive: boolean) => {
        this.isDecorModeActive = isActive;
        this.updateDecorButtonState();
      }),
      EventBus.on('SET_SHORTCUTS_BADGE', (show: boolean) => {
        const badge = this.container.querySelector('#nav-shortcuts-badge');
        if (badge) {
          if (show) badge.classList.remove('hidden');
          else badge.classList.add('hidden');
        }
      })
    );
  }

  public destroy(): void {
    for (const unsub of this.unsubscribes) {
      unsub();
    }
    this.unsubscribes = [];
  }

  private handleDebouncedTap(action: () => void): void {
    const now = Date.now();
    if (now - this.lastTapTimestamp < this.DEBOUNCE_MS) return;
    this.lastTapTimestamp = now;
    action();
  }

  private onMapTap(): void {
    if (this.currentScene !== 'HomeScene') return;
    if (this.isDecorModeActive && this.currentScene === 'HomeScene') {
      EventBus.emit('REQUEST_LEAVE_DECOR_TO_MAP');
      return;
    }
    EventBus.emit('NAVIGATE_TO_MAP');
  }

  private onDecorTap(): void {
    if (this.currentScene !== 'HomeScene') {
      EventBus.emit('SHOW_TOAST', {
        message: 'Chỉ có thể trang trí khi đang ở trong phòng Home!',
        type: 'warning'
      });
      return;
    }
    this.isDecorModeActive = !this.isDecorModeActive;
    EventBus.emit('TOGGLE_DECOR_MODE', this.isDecorModeActive);
    this.updateDecorButtonState();
  }

  private onShortcutsTap(): void {
    EventBus.emit('OPEN_SHORTCUTS_MODAL');
  }

  private onHomeTap(): void {
    if (this.currentScene === 'HomeScene') {
      EventBus.emit('CAMERA_RECENTER');
    } else {
      EventBus.emit('NAVIGATE_TO_HOME');
    }
  }

  public updateSceneState(sceneId: ActiveSceneId): void {
    this.currentScene = sceneId;

    if (sceneId !== 'HomeScene') {
      this.isDecorModeActive = false;
      this.btnMap.classList.add('active', 'nav-btn-hidden');

      this.btnDecor.classList.add('disabled', 'nav-btn-hidden');
      this.btnDecor.setAttribute('disabled', 'true');

      this.btnHome.classList.remove('nav-btn-hidden');
      this.btnHome.classList.add('highlight-pulse');
      this.btnHome.querySelector('.nav-label')!.textContent = 'Về Phòng ↩';

      this.btnShortcuts.classList.remove('nav-btn-hidden');
    } else {
      this.btnMap.classList.remove('active', 'nav-btn-hidden');

      this.btnDecor.classList.remove('disabled', 'nav-btn-hidden');
      this.btnDecor.removeAttribute('disabled');

      this.btnHome.classList.remove('highlight-pulse');
      this.btnHome.classList.add('nav-btn-hidden');
      this.btnHome.querySelector('.nav-label')!.textContent = 'Về Phòng';

      this.btnShortcuts.classList.add('nav-btn-hidden');
    }

    this.updateDecorButtonState();
  }

  private updateDecorButtonState(): void {
    if (this.isDecorModeActive && this.currentScene === 'HomeScene') {
      this.container.classList.add('nav-dock--decor-hidden');
      this.btnDecor.classList.add('active');
      this.btnDecor.querySelector('.nav-label')!.textContent = 'Xong';
      this.btnDecor.querySelector('.nav-icon')!.innerHTML = renderUiGouacheIcon('icon_check_seal', {
        size: 28,
        className: 'nav-gouache-icon',
      });
    } else {
      this.container.classList.remove('nav-dock--decor-hidden');
      this.btnDecor.classList.remove('active');
      this.btnDecor.querySelector('.nav-label')!.textContent = 'Trang trí';
      this.btnDecor.querySelector('.nav-icon')!.innerHTML = renderUiGouacheIcon('icon_decor_palette', {
        size: 28,
        className: 'nav-gouache-icon',
      });
    }
  }
}
